import * as React from 'react';
import { DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AdminNavItem } from '@/components/admin-nav-item';
import { navSectionLabel } from '@/lib/admin-nav-sections';
import type { PermissionCatalogEntry } from '@/lib/types';

type AdminNavMenuPreview = {
  pageAccessGranted: boolean;
};

interface AdminNavMenuEntriesProps {
  entries: PermissionCatalogEntry[];
  activeRoute?: string;
  preview?: AdminNavMenuPreview;
  getPreview?: (entry: PermissionCatalogEntry) => AdminNavMenuPreview | undefined;
}

/**
 * Renders dropdown menu nav items with optional catalog `navSection` separators.
 */
export function AdminNavMenuEntries({ entries, activeRoute = '', preview, getPreview }: AdminNavMenuEntriesProps) {
  let previousSection: string | undefined;

  return (
    <>
      {entries.map((entry) => {
        const section = entry.navSection;
        const showSection = section && section !== previousSection;
        if (section) previousSection = section;
        const sectionLabel = showSection ? navSectionLabel(section) : undefined;

        return (
          <React.Fragment key={entry.id}>
            {showSection && sectionLabel ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">{sectionLabel}</DropdownMenuLabel>
              </>
            ) : null}
            <AdminNavItem
              entry={entry}
              activeRoute={activeRoute}
              variant="menu"
              preview={getPreview?.(entry) ?? preview}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

interface AdminNavMobileSectionLabelProps {
  section?: string;
  show: boolean;
}

/** Mobile sheet section divider when `navSection` changes within a group. */
export function AdminNavMobileSectionLabel({ section, show }: AdminNavMobileSectionLabelProps) {
  if (!show || !section) return null;
  const label = navSectionLabel(section);
  if (!label) return null;
  return (
    <p className="px-2 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
  );
}
